import { inject, onMounted, provide, unref, useSSRContext, watchEffect, type InjectionKey, type Ref } from 'vue';
import { ref } from 'vue';

export type MetaOptions = {
  title?: string;
  description?: string;
  robots?: string;
  canonical?: string;
  og?: {
    title?: string;
    description?: string;
    image?: string;
    type?: string;
    url?: string;
    siteName?: string;
    locale?: string;
  };
  twitter?: { card?: 'summary' | 'summary_large_image'; title?: string; description?: string; image?: string };
  jsonLd?: Array<Record<string, unknown>>;
  preloadImages?: Array<{ href: string; fetchPriority?: 'high' | 'low' | 'auto' }>;
};

type MaybeRef<T> = T | Ref<T>;

export type MetaInput = {
  title?: MaybeRef<string | null | undefined>;
  description?: MaybeRef<string | null | undefined>;
  robots?: MaybeRef<string | null | undefined>;
  canonical?: MaybeRef<string | null | undefined>;
  og?: {
    title?: MaybeRef<string | null | undefined>;
    description?: MaybeRef<string | null | undefined>;
    image?: MaybeRef<string | null | undefined>;
    type?: MaybeRef<string | null | undefined>;
    url?: MaybeRef<string | null | undefined>;
    siteName?: MaybeRef<string | null | undefined>;
    locale?: MaybeRef<string | null | undefined>;
  };
  twitter?: {
    card?: MaybeRef<'summary' | 'summary_large_image' | undefined>;
    title?: MaybeRef<string | null | undefined>;
    description?: MaybeRef<string | null | undefined>;
    image?: MaybeRef<string | null | undefined>;
  };
  jsonLd?: MaybeRef<Array<Record<string, unknown>> | undefined>;
  preloadImages?: MaybeRef<Array<{ href: string; fetchPriority?: 'high' | 'low' | 'auto' }> | undefined>;
};

export type MetaManager = {
  meta: Ref<MetaOptions>;
};

export const META_MANAGER_KEY: InjectionKey<MetaManager> = Symbol('meta-manager');

export function createMetaManager(initial?: MetaOptions): MetaManager {
  return { meta: ref(initial ?? {}) };
}

export function provideMetaManager(manager: MetaManager): void {
  provide(META_MANAGER_KEY, manager);
}

function applyClientMeta(meta: MetaOptions): void {
  if (typeof document === 'undefined') return;

  const managedElements = document.head.querySelectorAll('[data-meta-managed="1"]');
  managedElements.forEach((element) => element.remove());

  const setMeta = (selector: string, attrs: Record<string, string>) => {
    let element = document.head.querySelector(selector) as HTMLMetaElement | null;
    if (!element) {
      element = document.createElement('meta');
      document.head.appendChild(element);
    }

    element.dataset.metaManaged = '1';
    Object.entries(attrs).forEach(([key, value]) => element!.setAttribute(key, value));
  };

  const setCanonical = (href: string) => {
    let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }

    canonical.dataset.metaManaged = '1';
    canonical.setAttribute('href', href);

    let xDefault = document.head.querySelector('link[rel="alternate"][hreflang="x-default"]') as HTMLLinkElement | null;
    if (!xDefault) {
      xDefault = document.createElement('link');
      xDefault.setAttribute('rel', 'alternate');
      xDefault.setAttribute('hreflang', 'x-default');
      document.head.appendChild(xDefault);
    }

    xDefault.dataset.metaManaged = '1';
    xDefault.setAttribute('href', href);
  };

  if (meta.title) {
    document.title = meta.title;
  }

  if (meta.description) {
    setMeta('meta[name="description"]', { name: 'description', content: meta.description });
  }

  if (meta.robots) {
    setMeta('meta[name="robots"]', { name: 'robots', content: meta.robots });
  }

  if (meta.canonical) {
    setCanonical(meta.canonical);
  }

  if (meta.og?.title) {
    setMeta('meta[property="og:title"]', { property: 'og:title', content: meta.og.title });
  }

  if (meta.og?.description) {
    setMeta('meta[property="og:description"]', { property: 'og:description', content: meta.og.description });
  }

  if (meta.og?.image) {
    setMeta('meta[property="og:image"]', { property: 'og:image', content: meta.og.image });
  }

  if (meta.og?.type) {
    setMeta('meta[property="og:type"]', { property: 'og:type', content: meta.og.type });
  }

  if (meta.og?.url) {
    setMeta('meta[property="og:url"]', { property: 'og:url', content: meta.og.url });
  }

  if (meta.og?.siteName) {
    setMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: meta.og.siteName });
  }

  if (meta.og?.locale) {
    setMeta('meta[property="og:locale"]', { property: 'og:locale', content: meta.og.locale });
  }

  if (meta.twitter?.card) {
    setMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: meta.twitter.card });
  }

  if (meta.twitter?.title) {
    setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: meta.twitter.title });
  }

  if (meta.twitter?.description) {
    setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: meta.twitter.description });
  }

  if (meta.twitter?.image) {
    setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: meta.twitter.image });
  }

  for (const jsonLdBlock of meta.jsonLd ?? []) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.metaJsonLd = '1';
    script.dataset.metaManaged = '1';
    script.text = JSON.stringify(jsonLdBlock);
    document.head.appendChild(script);
  }

  for (const preloadImage of meta.preloadImages ?? []) {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = preloadImage.href;
    if (preloadImage.fetchPriority) {
      link.setAttribute('fetchpriority', preloadImage.fetchPriority);
    }
    link.dataset.metaManaged = '1';
    document.head.appendChild(link);
  }
}

export function useMeta(meta: MetaInput): void {
  const manager = inject(META_MANAGER_KEY);
  if (!manager) return;

  const normalizeNullable = <T>(value: T | null | undefined): T | undefined => {
    return value ?? undefined;
  };

  watchEffect(() => {
    const resolvedMeta: MetaOptions = {
      title: normalizeNullable(unref(meta.title)),
      description: normalizeNullable(unref(meta.description)),
      robots: normalizeNullable(unref(meta.robots)),
      canonical: normalizeNullable(unref(meta.canonical)),
      og: meta.og ? {
        title: normalizeNullable(unref(meta.og.title)),
        description: normalizeNullable(unref(meta.og.description)),
        image: normalizeNullable(unref(meta.og.image)),
        type: normalizeNullable(unref(meta.og.type)),
        url: normalizeNullable(unref(meta.og.url)),
        siteName: normalizeNullable(unref(meta.og.siteName)),
        locale: normalizeNullable(unref(meta.og.locale)),
      } : undefined,
      twitter: meta.twitter ? {
        card: unref(meta.twitter.card),
        title: normalizeNullable(unref(meta.twitter.title)),
        description: normalizeNullable(unref(meta.twitter.description)),
        image: normalizeNullable(unref(meta.twitter.image)),
      } : undefined,
      jsonLd: unref(meta.jsonLd),
      preloadImages: unref(meta.preloadImages),
    };

    manager.meta.value = { ...manager.meta.value, ...resolvedMeta };

    const ssr = useSSRContext() as { __meta?: MetaOptions } | undefined;
    if (ssr) {
      ssr.__meta = manager.meta.value;
    }
  });

  onMounted(() => {
    watchEffect(() => {
      applyClientMeta(manager.meta.value);
    });
  });
}
